import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Swal from 'sweetalert2';
import { getUsers } from '../mock/users';
import EditClientForm from './EditClientForm';

const ClientCard = ({ client, onSelect, onNewMeeting, onUpdateClient, onDeleteClient, currentUser }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const loadedUsers = await getUsers();
        setUsers(loadedUsers);
      } catch (error) {
        console.error('Error cargando usuarios:', error);
        setUsers([]);
      }
    };
    loadUserData();
  }, []);

  const totalPatients = client.meetings.reduce((total, meeting) => total + meeting.patients, 0);
  const totalContacts = client.meetings.reduce((total, meeting) => total + meeting.contactsGenerated, 0);
  const conversionRate = totalContacts > 0 ? (totalPatients / totalContacts * 100).toFixed(1) + '%' : '0%';

  // Función para generar el mismo PDF que el del historial
  const generateClientPDF = () => {
    // Mostrar mensaje al usuario
    alert('Redirigiendo al perfil del cliente para generar el PDF completo...');
    
    // Ir al perfil del cliente
    onSelect(client);
    
    // Esperar un momento para que se cargue el perfil y luego generar el PDF automáticamente
    setTimeout(() => {
      // Buscar el botón de generar PDF usando diferentes métodos
      let generateButton = null;
      
      // Método 1: Buscar por texto del botón
      const buttons = document.querySelectorAll('button');
      for (let button of buttons) {
        if (button.textContent && button.textContent.includes('Generar PDF Completo')) {
          generateButton = button;
          break;
        }
      }
      
      // Método 2: Buscar por función onclick
      if (!generateButton) {
        generateButton = document.querySelector('button[onclick*="generateProfessionalPDF"]');
      }
      
      // Método 3: Buscar por clase o atributo específico
      if (!generateButton) {
        generateButton = document.querySelector('[data-testid="generate-pdf-button"]');
      }
      
      if (generateButton) {
        generateButton.click();
      } else {
        // Si no encuentra el botón, mostrar mensaje al usuario
        setTimeout(() => {
          alert('Por favor, haz clic en el botón "Generar PDF Completo" para obtener el expediente completo con todas las gráficas y secciones.');
        }, 500);
      }
    }, 1500); // Dar tiempo suficiente para que se cargue el perfil completo
  };

  if (isEditing) {
    return (
      <EditClientForm
        client={client}
        onSave={(updatedClient) => {
          onUpdateClient(updatedClient);
          setIsEditing(false);
        }}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-[#0061A8]">{client.name}</h3>
              <span className="text-xs px-2 py-1 bg-[#F8F9FA] text-[#63666A] rounded-full">
                {client.meetings.length} juntas
              </span>
            </div>
            <div className="mt-2 space-y-1">
              {client.specialty && (
                <div className="flex items-center text-sm text-[#63666A]">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                  {client.specialty}
                </div>
              )}
              {client.cell && (
                <div className="flex items-center text-sm text-[#63666A]">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  {client.cell}
                </div>
              )}
              {currentUser.role === 'admin' && (
                <div className="flex items-center text-sm text-[#A8D08D]">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {(() => {
                    const user = users.find(u => u.email === client.salesExecutive);
                    return user ? `${user.firstName} ${user.lastName}` : client.salesExecutive;
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-[#F8F9FA] rounded-lg p-4 mb-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-[#63666A] mb-1">Contactos</p>
              <p className="font-semibold text-[#0061A8]">{totalContacts}</p>
            </div>
            <div>
              <p className="text-xs text-[#63666A] mb-1">Pacientes</p>
              <p className="font-semibold text-[#0061A8]">{totalPatients}</p>
            </div>
            <div>
              <p className="text-xs text-[#63666A] mb-1">Conversión</p>
              <p className="font-semibold text-[#0061A8]">{conversionRate}</p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onSelect(client)}
              className="text-sm text-[#0061A8] hover:bg-[#F8F9FA] py-2 px-3 rounded-md border border-[#0061A8] flex items-center justify-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Ver Historial
            </button>
            <button
              onClick={() => onNewMeeting(client)}
              className="text-sm text-white bg-[#0061A8] hover:bg-opacity-90 py-2 px-3 rounded-md flex items-center justify-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Nueva Junta
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={generateClientPDF}
              className="text-sm text-white bg-[#A8D08D] hover:bg-opacity-90 py-2 px-3 rounded-md flex items-center justify-center"
              title="Genera el PDF completo con todas las secciones: Rendimiento General, Historial de Juntas, Rendimiento Mensual, Análisis de Objeciones, Servicios Vendidos y Minuta Ejecutiva"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              PDF Completo
            </button>
            <button
              onClick={() => setIsEditing(true)}
              className="text-sm text-white bg-[#0061A8] hover:bg-opacity-90 py-2 px-3 rounded-md flex items-center justify-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Editar Cliente
            </button>
          </div>
          {currentUser.role === 'admin' && (
            <button
              onClick={() => onDeleteClient(client.id)}
              className="w-full text-sm text-white bg-red-500 hover:bg-opacity-90 py-2 px-3 rounded-md flex items-center justify-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Eliminar Cliente
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientCard;
